import React, { useState, useEffect, useCallback } from 'react';
import { Box, TextField, Typography, Divider, InputAdornment, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import { motion, AnimatePresence } from 'framer-motion';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  GoogleAuthProvider, 
  OAuthProvider,
  signInWithPopup,
  setPersistence, 
  browserLocalPersistence,
  onAuthStateChanged,
  onIdTokenChanged
} from 'firebase/auth';
import { isAuthorized } from './AuthorizedUsers';
import { saveUserEmailToPreferences, getUserProfile } from '../services/AGTHallService';
import { updateIntercomUser } from '../../common/services/IntercomService';

const firebaseConfig = {
  apiKey: "AIzaSyAAI5uM8kBLooPT1Fkdk3CmJW4uWfyDVr8",
  authDomain: "agtapp-com.firebaseapp.com",
  projectId: "agtapp-com",
  storageBucket: "agtapp-com.firebasestorage.app",
  messagingSenderId: "1079810855217",
  appId: "1:1079810855217:web:4935b0ee3d990669022010",
  measurementId: "G-G8T8VRM3NB"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

setPersistence(auth, browserLocalPersistence);

const AuthContainer = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px',
  width: '100%',
  maxWidth: '420px',
  margin: '0 auto',
  marginTop: '10vh',
  borderRadius: '16px',
  position: 'relative',
  zIndex: 10,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(30, 30, 30, 0.6)' 
    : 'rgba(255, 255, 255, 0.6)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(27, 14, 14, 0.1)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px 0 rgba(255, 255, 255, 0.05)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
  color: theme.palette.colors.label.primary,
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
}));

const StyledTypography = styled(Typography)({
  color: 'var(--label-primary)',
  '&.login-text': {
    fontSize: '14px',
    transition: 'opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '& span': {
      color: 'var(--ui-accent)',
      cursor: 'pointer',
      fontSize: 'inherit',
      fontWeight: 600,
      transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -2,
        left: 0,
        width: '0%',
        height: '2px',
        backgroundColor: 'var(--ui-accent)',
        transition: 'width 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      },
      '&:hover': {
        transform: 'translateY(-1px)',
        '&::after': {
          width: '100%',
        },
      },
    },
  },
});

const RoundedTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'var(--ui-base)',
    border: '1px solid var(--ui-base)',
    color: 'var(--label-primary)',
    boxShadow: 'none',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    '& fieldset': {
      border: 'none',
    },
    '&:hover': {
      backgroundColor: 'var(--grouped-bg-primary-base)',
      border: '1px solid var(--system-grey3)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      border: '2px solid var(--ui-accent)',
      backgroundColor: 'var(--grouped-bg-primary-base)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(3, 133, 255, 0.15)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--label-secondary)',
    fontWeight: 500,
    marginLeft: '4px',
    fontSize: '0.9rem',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    transform: 'translate(14px, 14px) scale(1)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -15px) scale(0.75)', // <-- Cambia de -9px a -12px o -15px
      padding: '0 6px',
      marginLeft: '0px',
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: 'var(--ui-accent)',
  },
  '& .MuiInputBase-input': {
    padding: '14px 16px',
    color: 'var(--label-primary)',
    fontWeight: 500,
    '&::placeholder': {
      color: 'var(--label-secondary)',
      opacity: 0.7,
    },
  },
  marginBottom: '12px',
  width: '100%',
  '& .MuiFormLabel-asterisk': {
    display: 'none',
  },
});

const AnimatedBox = styled(motion.div)({
  overflow: 'visible',
  width: '100%',
});

const AuthButton = styled(motion.button)({
  borderRadius: '12px',
  marginTop: '8px',
  marginBottom: '16px',
  backgroundColor: 'var(--ui-accent)',
  color: 'var(--system-white)',
  height: '50px',
  width: '100%',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.95rem',
  boxShadow: '0 2px 8px rgba(3, 133, 255, 0.25)',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  border: 'none',
  cursor: 'pointer',
  '&:hover:not(:disabled)': {
    backgroundColor: 'var(--fill-tertiary)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(3, 133, 255, 0.35)',
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(3, 133, 255, 0.25)',
  },
  '&:disabled': {
    backgroundColor: 'var(--fill-quaternary)',
    color: 'var(--label-tertiary)',
    cursor: 'not-allowed',
    boxShadow: 'none',
    opacity: 0.6,
  }
});

const SocialButton = styled(motion.button)({
  borderRadius: '12px',
  marginTop: '8px',
  marginBottom: '8px',
  backgroundColor: 'var(--bg-primary-elevated)',
  color: 'var(--label-primary)',
  height: '50px',
  width: '100%',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.95rem',
  border: '1px solid var(--system-grey3)',
  boxShadow: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  '&:hover:not(:disabled)': {
    backgroundColor: 'var(--grouped-bg-primary-base)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    borderColor: 'var(--ui-accent)',
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.06)',
  },
  '&:disabled': {
    opacity: 0.85,
    cursor: 'not-allowed',
  }
});

const ErrorMessage = styled(motion.div)({
  color: 'var(--system-grey)',
  backgroundColor: 'var(--grouped-bg-primary-base)',
  padding: '12px 16px',
  borderRadius: '10px',
  marginTop: '16px',
  width: '100%',
  textAlign: 'center',
  border: '1px solid var(--system-grey3)',
  fontSize: '0.9rem',
});

const StyledDivider = styled(Divider)({
  margin: '20px 0',
  width: '100%',
  '&.MuiDivider-root': {
    '&::before, &::after': {
      borderTop: '1px solid var(--system-grey3)',
    },
  },
  '& .MuiDivider-wrapper': {
    color: 'var(--label-secondary)',
    padding: '0 10px',
  },
});

// Google Logo SVG como componente React
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Iconos de visibilidad para el campo de password
const VisibilityIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const VisibilityOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

// Logo de Apple personalizado como componente React
const AppleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 384 512">
    <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

// Variantes de animación para Framer Motion
const containerVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: { 
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -20,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 20
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const titleVariants = {
  hidden: { 
    opacity: 0,
    y: -10
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const passwordVariants = {
  hidden: { 
    opacity: 0,
    height: 0,
    marginBottom: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  visible: { 
    opacity: 1,
    height: 'auto',
    marginBottom: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const buttonHover = {
  scale: 1.02,
  transition: {
    duration: 0.2,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

const buttonTap = {
  scale: 0.98,
  transition: {
    duration: 0.1,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

const Auth = ({ onLogin, mode, onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const handleSuccessfulLogin = useCallback(async (user) => {
    try {
      if (user && typeof user.getIdToken === 'function') {
        const token = await user.getIdToken();
        sessionStorage.setItem('authToken', token);

        const userEmail = user.email;
        const userId = user.uid;
        const displayName = user.displayName || userEmail?.split('@')[0];

        await saveUserEmailToPreferences({
          userId,
          email: userEmail || '',
          displayName: displayName,
        });

        // Load full profile from DynamoDB to get updated photoURL and displayName
        let enrichedUser = user;
        try {
          const profileData = await getUserProfile(userId);
          if (profileData && profileData.profile) {
            // Enrich the user object with data from DynamoDB
            enrichedUser = {
              ...user,
              displayName: profileData.profile.displayName || user.displayName,
              photoURL: profileData.profile.photoURL || user.photoURL,
              email: profileData.profile.email || user.email
            };
          }
        } catch (profileError) {
          console.error('Error loading user profile from DynamoDB:', profileError);
          // Continue with Firebase user data if DynamoDB fails
        }

        updateIntercomUser({
          uid: userId,
          email: enrichedUser.email,
          displayName: enrichedUser.displayName,
          metadata: user.metadata
        });

        const isUserAuthorized = isAuthorized(user.email);
        onLogin(enrichedUser, isUserAuthorized);
      } else {
        throw new Error('Invalid user or getIdToken method not available');
      }
    } catch (error) {
      console.error('Error getting token', error);
      setMessage('Login error. Please try again.');
    }
  }, [onLogin]);

  useEffect(() => {
    const unsubscribeAuthState = onAuthStateChanged(auth, (user) => {
      if (user) {
        handleSuccessfulLogin(user);
      }
    });

    const unsubscribeIdToken = onIdTokenChanged(auth, async (user) => {
      if (user) {
        const token = await user.getIdToken();
        sessionStorage.setItem('authToken', token);
      } else {
        sessionStorage.removeItem('authToken');
      }
    });

    return () => {
      unsubscribeAuthState();
      unsubscribeIdToken();
    };
  }, [handleSuccessfulLogin]);

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setShowPassword(emailRegex.test(email));
  }, [email]);

  const handleToggleMode = () => {
    setMessage('');
    setEmail('');
    setPassword('');
    onToggleMode();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      let result;
      if (mode === 'login') {
        result = await signInWithEmailAndPassword(auth, email, password);
        setMessage('Login successful');
      } else {
        result = await createUserWithEmailAndPassword(auth, email, password);
        setMessage('User registered successfully');
      }
      await handleSuccessfulLogin(result.user);
    } catch (error) {
      console.error('Authentication error', error);
      setMessage(getErrorMessage(error.code));
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      let authProvider;
      if (provider === 'google') {
        authProvider = new GoogleAuthProvider();
        authProvider.addScope('https://www.googleapis.com/auth/userinfo.email');
        authProvider.addScope('https://www.googleapis.com/auth/userinfo.profile');
        authProvider.setCustomParameters({
          prompt: 'select_account'
        });
      } else if (provider === 'apple') {
        authProvider = new OAuthProvider('apple.com');
        authProvider.addScope('email');
        authProvider.addScope('name');
      } else {
        throw new Error('Unsupported provider');
      }

      const result = await signInWithPopup(auth, authProvider);

      // Intentar cerrar la ventana popup si existe (puede fallar por políticas COOP)
      try {
        if (window.opener && !window.opener.closed) {
          window.close();
        }
      } catch (coopError) {
        // Ignorar errores de Cross-Origin-Opener-Policy - son esperados
        console.debug('COOP policy restricts window access (expected)');
      }

      await handleSuccessfulLogin(result.user);
      setMessage(`Successfully signed in with ${provider}`);
    } catch (error) {
      // Ignore when user closes the popup - this is not an error
      if (error.code === 'auth/popup-closed-by-user') {
        return;
      }
      console.error('Social login error', error);
      setMessage(getErrorMessage(error.code));
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email address.';
      case 'auth/weak-password':
        return 'The password is too weak.';
      case 'auth/invalid-email':
        return 'Invalid email format.';
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with this email using a different sign-in method.';
      case 'auth/popup-closed-by-user':
        return 'The sign-in window was closed before completing the operation.';
      default:
        return 'An error occurred during authentication. Please try again.';
    }
  };

  return (
    <AuthContainer
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          variants={titleVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{ width: '100%', marginBottom: '16px', textAlign: 'center' }}
        >
          <StyledTypography variant="h5" sx={{ fontWeight: 'bold' }}>
            {mode === 'login' ? 'Welcome Back' : 'Join us'}
          </StyledTypography>
        </motion.div>
      </AnimatePresence>
      
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* TODO: Habilitar Apple Sign-In en futuro desarrollo
        <motion.div variants={itemVariants}>
          <SocialButton
            aria-label="Sign in with Apple"
            disabled
            whileHover={buttonHover}
            whileTap={buttonTap}
            sx={{
              backgroundColor: 'var(--bg-primary-elevated)',
              borderColor: 'var(--system-grey3)',
              height: '54px',
              '&.Mui-disabled': {
                opacity: 0.85,
                color: 'var(--label-primary)',
                border: '1px solid var(--system-grey3)',
              }
            }}>
            <AppleLogo />
            Continue with Apple
          </SocialButton>
        </motion.div>
        */}
        
        <motion.div variants={itemVariants}>
          <SocialButton
            onClick={() => handleSocialLogin('google')}
            aria-label="Sign in with Google"
            whileHover={buttonHover}
            whileTap={buttonTap}
            sx={{
              height: '50px',
              backgroundColor: 'var(--bg-primary-elevated)',
            }}>
            <GoogleLogo />
            Continue with Google
          </SocialButton>
        </motion.div>
      </Box>

      <motion.div variants={itemVariants} style={{ width: '100%' }}>
        <StyledDivider>
          <Typography variant="body2" sx={{ color: 'var(--label-secondary)' }}>
            Or
          </Typography>
        </StyledDivider>
      </motion.div>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
        <motion.div variants={itemVariants}>
          <RoundedTextField
            variant="outlined"
            required
            fullWidth
            id="email"
            label="Email address"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="youremail@example.com"
          />
        </motion.div>
        
        <AnimatePresence>
          {showPassword && (
            <AnimatedBox
              variants={passwordVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <RoundedTextField
                variant="outlined"
                required
                fullWidth
                id="password"
                label="Password"
                name="password"
                type={passwordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'login' ? '••••••••' : 'Create a password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                        onClick={() => setPasswordVisible(!passwordVisible)}
                        edge="end"
                        sx={{
                          color: 'var(--label-secondary)',
                          '&:hover': {
                            color: 'var(--label-primary)',
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </AnimatedBox>
          )}
        </AnimatePresence>
        
        <motion.div variants={itemVariants}>
          <AuthButton 
            type="submit"
            disabled={!showPassword || !password}
            whileHover={(!showPassword || !password) ? {} : buttonHover}
            whileTap={(!showPassword || !password) ? {} : buttonTap}
          >
            {mode === 'login' ? 'Continue' : 'Register'}
          </AuthButton>
        </motion.div>
        
        <motion.div 
          variants={itemVariants}
          style={{ textAlign: 'center' }}
        >
          <StyledTypography variant="body2" className="login-text">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <span onClick={handleToggleMode}>
              {mode === 'login' ? 'Sign Up' : 'Log In'}
            </span>
          </StyledTypography>
        </motion.div>
      </Box>

      <AnimatePresence>
        {message && (
          <ErrorMessage
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {message}
          </ErrorMessage>
        )}
      </AnimatePresence>
    </AuthContainer>
  );
};

export default Auth;